import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { useMutation } from "@apollo/client";
import { MERGE_SKELETON_USER, SYNC_SUPABASE_USER } from "../../../../queries";
import { getLocalStorage, getSupabaseSession, setLocalStorage } from "../../../../utils/common-function";
import { routes } from "../../../../routes/routes.constant";
import { ILocationState } from "./types";
import useUser from "../../../../services/hooks/useUser";
import { useAuth } from "../../../../store/contextAPI/authContext";

const useRedirectCallback = () => {
  const history = useHistory();
  const location = useLocation<ILocationState>();
  const [error, setError] = useState("");
  const [syncSupabaseUser] = useMutation(SYNC_SUPABASE_USER);
  const [mergeSkeletonUser] = useMutation(MERGE_SKELETON_USER);
  const [effectiveUserId, setEffectiveUserId] = useState<string | null>(() => getLocalStorage("userId"));
  const { userData } = useUser(effectiveUserId);
  const { updateUserProfile } = useUser(effectiveUserId);

  const [isLoading, setLoading] = useState(true);
  const hasRedirectedToOnboarding = useRef(false);
  const { setNameContext, setPasswordContext } = useAuth();

  const syncUser = useCallback(async (): Promise<string | null> => {
    const linkGuestIdentifier =
      getLocalStorage("linkGuestIdentifier") ??
      (location.state as { linkGuestIdentifier?: string } | undefined)?.linkGuestIdentifier;

    const result = await syncSupabaseUser({
      variables: { input: {} }
    });
    const newUserId = result?.data?.syncSupabaseUser?.user?.id;
    if (!newUserId) {
      setError("Unable to sync account. Please try again.");
      return null;
    }
    setLocalStorage("userId", newUserId);

    if (linkGuestIdentifier) {
      try {
        await mergeSkeletonUser({
          variables: { userIdentifier: linkGuestIdentifier, userId: newUserId }
        });
        localStorage.removeItem("linkGuestIdentifier");
        localStorage.removeItem("generatedUserId");
      } catch (mergeErr) {
        console.error("Error linking guest progress:", mergeErr);
        setError("Your game progress could not be linked. You can continue anyway.");
      }
    }
    return newUserId;
  }, [syncSupabaseUser, mergeSkeletonUser, location.state]);

  const getUserDetails = useCallback(async () => {
    const session = await getSupabaseSession();
    if (session) {
      setLocalStorage("token", session.access_token);
      const { email, name } = session.user.user_metadata;
      try {
        const newUserId = await syncUser();
        if (newUserId) {
          setEffectiveUserId(newUserId);
        }
      } catch (syncErr) {
        console.error("Error syncing user:", syncErr);
        setError("Unable to sync account. Please try again.");
      }
      if (name) {
        const [firstName, lastName = ""] = name.trim().split(" ");
        setPasswordContext("");
        setNameContext(firstName, lastName);
      }
      setLocalStorage("email", email);
      setLoading(false);
    } else {
      if (!hasRedirectedToOnboarding.current) {
        hasRedirectedToOnboarding.current = true;
        history.push({ pathname: routes.onboarding, state: { from: location.state?.from } });
      }
    }
  }, [history, location.state?.from, syncUser, setPasswordContext]);

  const onUpdateSuccess = useCallback(() => {
    setError("");
  }, [history, location.state]);

  const onUpdateError = useCallback(() => {
    setError("An error occurred while updating your profile. Please try again.");
  }, []);

  useEffect(() => {
    getUserDetails();
  }, []);

  useEffect(() => {
    if (userData) {
      const { finishedOnBoarding, fullName, birthdate, profilePhoto } = userData;
      if (finishedOnBoarding) {
        history.push({ pathname: location.state?.from ?? "/", state: location.state });
      } else {
        if (!fullName) {
          history.push({ pathname: routes.userName, state: location.state });
        } else if (!birthdate) {
          history.push({ pathname: routes.userDob, state: location.state });
        } else if (!profilePhoto) {
          history.push({ pathname: routes.addPhoto, state: location.state });
        } else {
          updateUserProfile({ finishedOnBoarding: true }, onUpdateSuccess, onUpdateError);
          history.push({ pathname: location.state?.from ?? "/", state: location.state });
        }
      }
    }
  }, [userData]);

  return {
    error,
    isLoading
  };
};

export default useRedirectCallback;
